import React, { useState, useEffect } from 'react';
import { planService } from '../../services/planService';
import { useLanguage } from '../../contexts/LanguageContext';
import { ArrowLeft, Save, Package } from 'lucide-react';

const buildInitialFeatureState = (catalog, plan) => {
    const state = {};
    for (const def of catalog) {
        const value = plan?.features?.[def.key];
        if (def.type === 'boolean') {
            state[def.key] = { enabled: Boolean(value) };
        } else if (def.type === 'quantity') {
            const isEnabled = value !== 0 && value !== undefined;
            state[def.key] = { enabled: isEnabled, limitValue: (typeof value === 'number' && value !== 0) ? value : null };
        } else if (def.type === 'multiselect') {
            const items = Array.isArray(value) ? value : [];
            state[def.key] = { enabled: items.length > 0, selectedItems: items };
        }
    }
    return state;
};

const PlanForm = ({ plan, catalog, onSaved, onCancel }) => {
    const { t, language } = useLanguage();
    const isEdit = Boolean(plan);
    const [nameTr, setNameTr] = useState(plan?.nameTr || '');
    const [nameEn, setNameEn] = useState(plan?.nameEn || '');
    const [descriptionTr, setDescriptionTr] = useState(plan?.descriptionTr || '');
    const [descriptionEn, setDescriptionEn] = useState(plan?.descriptionEn || '');
    const [monthlyPriceTry, setMonthlyPriceTry] = useState(plan?.monthlyPriceTry ?? 0);
    const [yearlyPriceTry, setYearlyPriceTry] = useState(plan?.yearlyPriceTry ?? 0);
    const [monthlyPriceUsd, setMonthlyPriceUsd] = useState(plan?.monthlyPriceUsd ?? 0);
    const [yearlyPriceUsd, setYearlyPriceUsd] = useState(plan?.yearlyPriceUsd ?? 0);
    const [isPopular, setIsPopular] = useState(plan?.isPopular || false);
    const [isActive, setIsActive] = useState(plan?.isActive ?? true);
    const [sortOrder, setSortOrder] = useState(plan?.sortOrder ?? 0);
    const [featureState, setFeatureState] = useState(() => buildInitialFeatureState(catalog, plan));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setFeatureState(buildInitialFeatureState(catalog, plan));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan]);

    const updateFeature = (key, patch) => setFeatureState(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        try {
            const payload = {
                nameTr, nameEn, descriptionTr, descriptionEn,
                monthlyPriceTry: parseFloat(monthlyPriceTry) || 0,
                yearlyPriceTry: parseFloat(yearlyPriceTry) || 0,
                monthlyPriceUsd: parseFloat(monthlyPriceUsd) || 0,
                yearlyPriceUsd: parseFloat(yearlyPriceUsd) || 0,
                isPopular, isActive, sortOrder: parseInt(sortOrder, 10) || 0,
                features: featureState
            };
            const response = isEdit ? await planService.updatePlan(plan.id, payload) : await planService.createPlan(payload);
            if (response.success) {
                onSaved();
            } else {
                setError(response.message || t('savePlanError'));
            }
        } catch (err) {
            setError(err.response?.data?.message || t('serverError'));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="plan-form-container">
            <div className="form-header">
                <button className="back-btn" onClick={onCancel}>
                    <ArrowLeft size={20} /> {t('cancel')}
                </button>
                <h2><Package size={24} /> {isEdit ? `${language === 'tr' ? plan.nameTr : plan.nameEn} - ${t('editPlanTitle')}` : t('newPlan')}</h2>
            </div>
            <p className="form-subtitle">{t('planSubtitle')}</p>

            <form onSubmit={handleSubmit} className="plan-form">
                {error && <div className="form-error">{error}</div>}

                <div className="form-section-title">{t('planGeneralInfo')}</div>
                <div className="form-row">
                    <div className="form-group">
                        <label>{t('nameTurkish')}</label>
                        <input type="text" value={nameTr} onChange={e => setNameTr(e.target.value)} required />
                    </div>
                    <div className="form-group">
                        <label>{t('nameEnglish')}</label>
                        <input type="text" value={nameEn} onChange={e => setNameEn(e.target.value)} required />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label>{t('descriptionTurkish')}</label>
                        <input type="text" value={descriptionTr} onChange={e => setDescriptionTr(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>{t('descriptionEnglish')}</label>
                        <input type="text" value={descriptionEn} onChange={e => setDescriptionEn(e.target.value)} />
                    </div>
                </div>

                <div className="form-section-title">{t('planPricing')}</div>
                <div className="form-row form-row-3">
                    <div className="form-group">
                        <label>{t('monthlyTry')}</label>
                        <input type="number" step="0.01" value={monthlyPriceTry} onChange={e => setMonthlyPriceTry(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>{t('yearlyTry')}</label>
                        <input type="number" step="0.01" value={yearlyPriceTry} onChange={e => setYearlyPriceTry(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>{t('sortOrder')}</label>
                        <input type="number" value={sortOrder} onChange={e => setSortOrder(e.target.value)} />
                    </div>
                </div>
                <div className="form-row">
                    <div className="form-group">
                        <label>{t('monthlyUsd')}</label>
                        <input type="number" step="0.01" value={monthlyPriceUsd} onChange={e => setMonthlyPriceUsd(e.target.value)} />
                    </div>
                    <div className="form-group">
                        <label>{t('yearlyUsd')}</label>
                        <input type="number" step="0.01" value={yearlyPriceUsd} onChange={e => setYearlyPriceUsd(e.target.value)} />
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={isPopular} onChange={e => setIsPopular(e.target.checked)} style={{ width: 'auto' }} />
                        <label style={{ margin: 0 }}>{t('showPopularBadge')}</label>
                    </div>
                    <div className="form-group" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} style={{ width: 'auto' }} />
                        <label style={{ margin: 0 }}>{t('planActiveHint')}</label>
                    </div>
                </div>

                <div className="form-section-title">{t('planFeatures')}</div>
                {catalog.map(def => (
                    <div className="form-group" key={def.key} style={{ borderBottom: '1px solid #f1f5f9', paddingBottom: '0.75rem' }}>
                        {def.type === 'boolean' && (
                            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <input
                                    type="checkbox"
                                    style={{ width: 'auto' }}
                                    checked={featureState[def.key]?.enabled || false}
                                    onChange={e => updateFeature(def.key, { enabled: e.target.checked })}
                                />
                                {language === 'tr' ? def.label_tr : def.label_en}
                            </label>
                        )}

                        {def.type === 'quantity' && (
                            <>
                                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <input
                                        type="checkbox"
                                        style={{ width: 'auto' }}
                                        checked={featureState[def.key]?.enabled || false}
                                        onChange={e => updateFeature(def.key, { enabled: e.target.checked })}
                                    />
                                    {language === 'tr' ? def.label_tr : def.label_en}
                                </label>
                                {featureState[def.key]?.enabled && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, marginLeft: 26 }}>
                                        <input
                                            type="number"
                                            min="0"
                                            style={{ width: 100 }}
                                            disabled={featureState[def.key]?.limitValue === null}
                                            value={featureState[def.key]?.limitValue ?? ''}
                                            placeholder={t('unlimited')}
                                            onChange={e => updateFeature(def.key, { limitValue: e.target.value === '' ? 0 : parseInt(e.target.value, 10) })}
                                        />
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, fontSize: '0.8rem' }}>
                                            <input
                                                type="checkbox"
                                                style={{ width: 'auto' }}
                                                checked={featureState[def.key]?.limitValue === null}
                                                onChange={e => updateFeature(def.key, { limitValue: e.target.checked ? null : 0 })}
                                            />
                                            {t('unlimited')}
                                        </label>
                                    </div>
                                )}
                            </>
                        )}

                        {def.type === 'multiselect' && (
                            <>
                                <label style={{ fontWeight: 600 }}>{language === 'tr' ? def.label_tr : def.label_en}</label>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginTop: 8 }}>
                                    {def.options.map(opt => (
                                        <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 400, fontSize: '0.85rem' }}>
                                            <input
                                                type="checkbox"
                                                style={{ width: 'auto' }}
                                                checked={featureState[def.key]?.selectedItems?.includes(opt) || false}
                                                onChange={e => {
                                                    const current = featureState[def.key]?.selectedItems || [];
                                                    const next = e.target.checked
                                                        ? [...current, opt]
                                                        : current.filter(i => i !== opt);
                                                    updateFeature(def.key, { selectedItems: next, enabled: next.length > 0 });
                                                }}
                                            />
                                            {opt}
                                        </label>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                ))}

                <div className="form-actions">
                    <button type="button" className="cancel-btn" onClick={onCancel}>{t('cancel')}</button>
                    <button type="submit" className="save-btn" disabled={loading}>
                        <Save size={16} /> {loading ? t('saving') : t('save')}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PlanForm;
